import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "react-toastify";
import { Download, Trash2 } from "lucide-react";
import ModalShell from "../modal/ModalShell";
import { useAppDispatch, useAppSelector } from "../../app/hooks";
import { selectIsOrgOwner } from "../../features/authentication/authenticationSlice";
import { selectOrganization } from "../../features/organization/organization-slice";
import { fetchCurrentOrganization } from "../../features/organization/slice-extensions/organization-slice-extension";
import { selectAccountDeletionScheduledFor } from "../../features/account-deletion/account-deletion-slice";
import {
  cancelAccountDeletion,
  downloadAccountDeletionExport,
  scheduleAccountDeletion,
} from "../../features/account-deletion/slice-extensions/account-deletion-slice-extension";

/**
 * The Owner's "Delete my account" card: schedules deletion with a 14-day grace
 * window behind a high-friction, type-to-confirm dialog with an export offer.
 */
const DeleteAccountSection = () => {
  const { t, i18n } = useTranslation();
  const dispatch = useAppDispatch();
  const isOwner = useAppSelector(selectIsOrgOwner);
  const organization = useAppSelector(selectOrganization);
  const scheduledFor = useAppSelector(selectAccountDeletionScheduledFor);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [confirmText, setConfirmText] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (isOwner && !organization) {
      dispatch(fetchCurrentOrganization());
    }
  }, [isOwner, organization, dispatch]);

  if (!isOwner) {
    return null;
  }

  const organizationName = organization?.name ?? "";
  const confirmMatches = confirmText.trim() === organizationName && organizationName.length > 0;

  const handleSchedule = async () => {
    setBusy(true);
    try {
      await dispatch(scheduleAccountDeletion()).unwrap();
      setConfirmOpen(false);
      setConfirmText("");
      toast.success(t("accountDeletion.scheduledToast", "Deletion scheduled — you have 14 days to change your mind."));
    } catch (error) {
      toast.error((error as { message?: string })?.message || t("accountDeletion.failed", "Something went wrong"));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="with-border rounded-2xl bg-white p-4 dark:bg-primary-10 lg:p-6">
      <h2 className="flex items-center gap-2 text-lg font-semibold text-red-600 dark:text-red-400">
        <Trash2 size={20} />
        {t("accountDeletion.title", "Delete account")}
      </h2>

      {scheduledFor ? (
        <>
          <p className="mt-2 text-sm text-grey-30 dark:text-grey-70">
            {t("accountDeletion.pendingText", "Deletion is scheduled for {{date}}.", {
              date: new Intl.DateTimeFormat(i18n.language, { dateStyle: "long" }).format(
                new Date(scheduledFor),
              ),
            })}
          </p>
          <button
            type="button"
            className="btn btn-secondary mt-3"
            disabled={busy}
            onClick={() => dispatch(cancelAccountDeletion())}
          >
            {t("accountDeletion.bannerCancel", "Cancel the deletion")}
          </button>
        </>
      ) : (
        <>
          <p className="mt-2 text-sm text-grey-30 dark:text-grey-70">
            {t(
              "accountDeletion.intro",
              "Permanently deletes this organization and everything in it after a 14-day grace period. Your subscription stops renewing immediately.",
            )}
          </p>
          <button
            type="button"
            className="btn btn-secondary mt-3 text-red-600 dark:text-red-400"
            onClick={() => setConfirmOpen(true)}
          >
            {t("accountDeletion.openButton", "Delete my account…")}
          </button>
        </>
      )}

      {confirmOpen && (
        <ModalShell onClose={() => setConfirmOpen(false)} labelledBy="delete-account-title">
          <div className="p-6">
            <h2 id="delete-account-title" className="text-lg font-bold text-red-600 dark:text-red-400">
              {t("accountDeletion.confirmTitle", "Delete this account?")}
            </h2>
            <p className="mt-2 text-sm text-grey-30 dark:text-grey-70">
              {t(
                "accountDeletion.confirmDestroys",
                "In 14 days, this permanently destroys: all sessions and attendance, all members and their data, all contracts and payments, your website and settings, and every instructor account in this organization.",
              )}
            </p>
            <p className="mt-2 text-sm text-grey-30 dark:text-grey-70">
              {t(
                "accountDeletion.confirmGrace",
                "Until then you can cancel the deletion with one click — signing in shows a banner with the date.",
              )}
            </p>

            <button
              type="button"
              className="btn btn-secondary mt-4"
              onClick={() => dispatch(downloadAccountDeletionExport())}
            >
              <Download size={16} />
              {t("accountDeletion.exportButton", "Download everything first (JSON)")}
            </button>

            <label className="mt-4 block text-sm font-medium text-grey-30 dark:text-grey-70">
              {t("accountDeletion.confirmTypeLabel", "Type your organization name to confirm: {{name}}", {
                name: organizationName,
              })}
              <input
                type="text"
                className="input mt-1"
                value={confirmText}
                onChange={(event) => setConfirmText(event.target.value)}
              />
            </label>

            <div className="mt-6 flex justify-end gap-2">
              <button
                type="button"
                className="btn btn-secondary"
                disabled={busy}
                onClick={() => setConfirmOpen(false)}
              >
                {t("common.cancel", "Cancel")}
              </button>
              <button
                type="button"
                className="btn bg-red-600 text-white hover:bg-red-700 dark:bg-red-700 dark:hover:bg-red-600"
                disabled={!confirmMatches || busy}
                onClick={handleSchedule}
              >
                {busy
                  ? t("accountDeletion.scheduling", "Scheduling…")
                  : t("accountDeletion.confirmButton", "Schedule deletion")}
              </button>
            </div>
          </div>
        </ModalShell>
      )}
    </div>
  );
};

export default DeleteAccountSection;
